import { FC, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  ScrollArea,
} from "@capacit-com/lara-law-shared-ui";
import { Building2, User, Check, Copy } from "lucide-react";
import { Dashboard, WidgetInstance } from "@/models/dashboard/Dashboard";
import { useDashboards, useUpdateDashboard } from "@/hooks/useDashboard";
import { serializeDashboardConfig } from "@/models/dashboard/Dashboard";
import { useHasPermission } from "@/lib/permissions/permissionFunctions";
import { Permission } from "@/lib/permissions/Permission.enum";
import { toast } from "@capacit-com/lara-law-shared-ui";
import { logger } from "@/lib/logger";
import { uuidv4 } from "@capacit-com/lara-law-shared-ui/uuid";

interface CopyWidgetToDashboardDialogProps {
  widget: WidgetInstance | null;
  sourceDashboardId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CopyWidgetToDashboardDialog: FC<CopyWidgetToDashboardDialogProps> = ({
  widget,
  sourceDashboardId,
  open,
  onOpenChange,
}) => {
  const { data: dashboards } = useDashboards();
  const updateDashboard = useUpdateDashboard();
  const hasPermission = useHasPermission();
  const canEditOrgDashboards = hasPermission({ allOf: [Permission.UpdateDashboardLayout] });

  const [selectedDashboardId, setSelectedDashboardId] = useState<string | null>(null);

  // Editable dashboards the user can copy into (exclude source and parent-tenant read-only)
  const targetDashboards: Dashboard[] = (dashboards ?? []).filter((d) => {
    if (d.id === sourceDashboardId) return false;
    if (d.isFromParentTenant) return false;
    if (d.isOrganizationDashboard && !canEditOrgDashboards) return false;
    return true;
  });

  const orgTargets = targetDashboards.filter((d) => d.isOrganizationDashboard);
  const personalTargets = targetDashboards.filter((d) => !d.isOrganizationDashboard);

  const handleCopy = async () => {
    if (!widget || !selectedDashboardId) return;

    const target = dashboards?.find((d) => d.id === selectedDashboardId);
    if (!target) return;

    const newWidget: WidgetInstance = {
      ...widget,
      id: uuidv4(),
      position: { x: 0, y: Infinity, w: widget.position.w, h: widget.position.h },
    };

    const updatedConfig = {
      ...target.config,
      widgets: [...target.config.widgets, newWidget],
    };

    try {
      await updateDashboard.mutateAsync({
        id: selectedDashboardId,
        request: {
          name: target.name,
          configJson: serializeDashboardConfig(updatedConfig),
        },
      });
      onOpenChange(false);
      setSelectedDashboardId(null);
      toast.success("Widget kopieret", {
        description: `"${widget.name}" er kopieret til "${target.name}".`,
      });
    } catch (e) {
      logger.error("Error copying widget to dashboard", {
        operation: "copyWidgetToDashboard",
        error: e,
        widgetId: widget.id,
        targetDashboardId: selectedDashboardId,
      });
      toast.error("Fejl", {
        description: "Der opstod en fejl ved kopiering af widget.",
      });
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) setSelectedDashboardId(null);
    onOpenChange(open);
  };

  const DashboardRow = ({ dashboard }: { dashboard: Dashboard }) => {
    const isSelected = selectedDashboardId === dashboard.id;
    return (
      <div
        className={`border rounded-lg p-3 cursor-pointer transition-colors flex items-center justify-between ${
          isSelected
            ? "border-primary bg-primary/5"
            : "hover:border-muted-foreground/50 hover:bg-muted/50"
        }`}
        onClick={() => setSelectedDashboardId(dashboard.id)}
      >
        <div className="flex items-center gap-3">
          <div className="p-1.5 rounded-md bg-muted">
            {dashboard.isOrganizationDashboard
              ? <Building2 className="h-4 w-4" />
              : <User className="h-4 w-4" />}
          </div>
          <div>
            <p className="text-sm font-medium">{dashboard.name}</p>
            <p className="text-xs text-muted-foreground">
              {dashboard.config.widgets.length} widget{dashboard.config.widgets.length !== 1 ? "s" : ""}
            </p>
          </div>
        </div>
        {isSelected && (
          <div className="p-1 rounded-full bg-primary text-primary-foreground shrink-0">
            <Check className="h-3 w-3" />
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="h-5 w-5" />
            Kopier widget til dashboard
          </DialogTitle>
          <DialogDescription>
            Vælg hvilket dashboard du vil kopiere{" "}
            <span className="font-medium">"{widget?.name}"</span> til.
          </DialogDescription>
        </DialogHeader>

        {targetDashboards.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground text-sm">
            Ingen tilgængelige dashboards at kopiere til.
          </div>
        ) : (
          <ScrollArea className="max-h-[320px] pr-1">
            <div className="space-y-4 py-2">
              {orgTargets.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide px-1">
                    Organisation
                  </p>
                  {orgTargets.map((d) => <DashboardRow key={d.id} dashboard={d} />)}
                </div>
              )}
              {personalTargets.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide px-1">
                    Personlige
                  </p>
                  {personalTargets.map((d) => <DashboardRow key={d.id} dashboard={d} />)}
                </div>
              )}
            </div>
          </ScrollArea>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Annuller
          </Button>
          <Button
            onClick={handleCopy}
            disabled={!selectedDashboardId || updateDashboard.isPending}
          >
            <Copy className="h-4 w-4 mr-2" />
            Kopier widget
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CopyWidgetToDashboardDialog;
